import React from 'react';
import { useTranslation } from 'react-i18next';

import Heading from '@atoms/Heading';
import HeadingSize from '@atoms/Heading/sizes';
import ButtonType from '@atoms/Button/type';
import { ButtonProps } from '@atoms/Button';
import NextPrevButtons from '@molecules/NextPrevButtons';
import Input from '@molecules/Input';

import { ReactComponent as BigBoxIcon } from '@icons/box--big.svg';
import { ReactComponent as BigBagIcon } from '@icons/cement-bag--big.svg';
import { ReactComponent as BigOverlapBagIcon } from '@icons/overlap-bag--big.svg';

import { ReactComponent as BoxIcon } from '@icons/box.svg';
import { ReactComponent as CementBagIcon } from '@icons/cement-bag.svg';

import useCalculatorState from 'hooks/useCalculatorState';
import { UnitDimension, UnitPackaging, UnitWeight } from 'enums/calculator';

import GrayContainer from '@atoms/GrayContainer';
import Selector from '@molecules/Selector';
import ToggleButtons from '@molecules/ToggleButtons';
import useBreakpoint from 'hooks/useBreakpoint';
import styles from './styles';

const Step1 = () => {
  const { t } = useTranslation();
  const {
    packagingType,
    dimensions,
    weight,

    setPackagingType,
    setDimensionType,
    setWeightType,
    setDimensionWidth,
    setDimensionHeight,
    setDimensionLength,
    setWeightValue,
    isFirstStepValid,
  } = useCalculatorState();

  const {
    isMobile,
  } = useBreakpoint();

  const {
    widthError,
    lengthError,
    heightError,
  } = dimensions || {};

  const {
    error: weightError,
  } = weight || {};

  const packagingTypeButtons: ButtonProps[] = [
    {
      type: ButtonType.OUTLINE,
      children: UnitPackaging.BOX,
      active: packagingType === UnitPackaging.BOX,
      icon: BoxIcon,
      className: 'flex justify-center items-center',
    },
    {
      type: ButtonType.OUTLINE,
      children: UnitPackaging.BAG,
      active: packagingType === UnitPackaging.BAG || packagingType === UnitPackaging.OVERLAP_BAG,
      icon: CementBagIcon,
      className: 'flex justify-center items-center',
    },
  ];
  const packagingTypeChange = (item: ButtonProps) => setPackagingType(
    item.children as UnitPackaging,
  );

  const unitDimensionsOptions: ButtonProps[] = [
    {
      type: ButtonType.PILL,
      children: UnitDimension.MM,
      active: dimensions.type === UnitDimension.MM,
      className: styles.toggleButton1,
    },
    {
      type: ButtonType.PILL,
      children: UnitDimension.INCH,
      active: dimensions.type === UnitDimension.INCH,
      className: styles.toggleButton2,
    },
  ];
  const unitDimensionsChange = (item: string) => setDimensionType(
    item as UnitDimension,
  );

  const unitWeightOptions: ButtonProps[] = [
    {
      type: ButtonType.PILL,
      children: UnitWeight.KG,
      active: weight.type === UnitWeight.KG,
      className: styles.toggleButton1,
    },
    {
      type: ButtonType.PILL,
      children: UnitWeight.LBS,
      active: weight.type === UnitWeight.LBS,
      className: styles.toggleButton2,
    },
  ];
  const unitWeightChange = (item: string) => setWeightType(
    item as UnitWeight,
  );

  const widthChange = (value: number) => setDimensionWidth(value);
  const lengthChange = (value: number) => setDimensionLength(value);
  const heightChange = (value: number) => setDimensionHeight(value);
  const weightChange = (value: number) => setWeightValue(value);

  let rightSideChildren;
  switch (packagingType) {
    case UnitPackaging.BOX:
      rightSideChildren = <BigBoxIcon className="max-w-full h-auto" />;
      break;

    case UnitPackaging.BAG:
    case UnitPackaging.OVERLAP_BAG:
      rightSideChildren = <BigBagIcon className="max-w-full h-auto" />;
      break;

    default:
      rightSideChildren = <BigOverlapBagIcon className="max-w-full h-auto" />;
      break;
  }

  const makeDimensionSelector = (tabbable: boolean = true) => (
    <Selector
      items={unitDimensionsOptions}
      onChange={unitDimensionsChange}
      selected={dimensions.type}
      tabbable={tabbable}
    />
  );

  const dimensionsSelectorNonTabbable = makeDimensionSelector(false);

  const weightSelector = (
    <Selector
      items={unitWeightOptions}
      onChange={unitWeightChange}
      selected={weight.type}
    />
  );

  return (
    <section className="md:flex justify-between md:gap-12 text-lg">
      <section className="user-input md:w-2/5">
        <section id="unit-information">
          <Heading size={HeadingSize.H1}>
            {t('home:title')}
          </Heading>
          <p>{t('home:step1.instructions')}</p>
          <Heading>{t('home:step1.packaging')}</Heading>
          <ToggleButtons
            className={styles.packageType}
            items={packagingTypeButtons}
            onChange={packagingTypeChange}
          />
          <div className={styles.toggleSection}>
            <Heading>{t('home:step1.dimensions')}</Heading>
          </div>
          <div className={styles.dimensionsInputsBlock}>
            <Input
              id="width"
              label={t('home:width')}
              placeholder={makeDimensionSelector()}
              onChange={widthChange}
              value={String(dimensions.width) || ''}
              error={widthError}
            />
            <Input
              id="length"
              label={t('home:length')}
              placeholder={dimensionsSelectorNonTabbable}
              onChange={lengthChange}
              value={String(dimensions.length) || ''}
              error={lengthError}
            />
            <Input
              className="col-span-2 sm:col-span-1"
              id="height"
              label={t('home:height')}
              placeholder={dimensionsSelectorNonTabbable}
              onChange={heightChange}
              value={String(dimensions.height) || ''}
              error={heightError}
            />
          </div>
          <div className={styles.toggleSection}>
            <Heading>{t('home:step1.weight')}</Heading>
          </div>
          <div>
            <Input
              id="weight"
              placeholder={weightSelector}
              onChange={weightChange}
              value={String(weight.value) || ''}
              error={weightError}
            />
          </div>
        </section>
        { !isMobile && <NextPrevButtons nextButtonEnabled={isFirstStepValid()} /> }
      </section>
      <GrayContainer className="md:w-3/5 my-14 md:my-0 md:!mb-24">
        {rightSideChildren}
      </GrayContainer>
      { isMobile && <NextPrevButtons nextButtonEnabled={isFirstStepValid()} /> }
    </section>
  );
};

export default Step1;
